'use client';

import { Skeleton } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useContextTabDirtyState } from '@/features/context-tabs';
import {
  useGetCurrentWorkspaceQuery,
  useLazyGetWorkspaceCurrenciesQuery,
  usePatchCurrentWorkspaceMutation,
  useRequestStorageUploadMutation,
} from '@/features/workspaces/api/workspacesApi';
import { IndustrySelectField } from '@/features/workspaces/components/selectors';
import { workspaceSettingsSchema } from '@/features/workspaces/schemas/workspaceSettingsSchema';
import { useAssetUpload } from '@web/hooks';
import {
  AutocompleteAsync,
  Avatar,
  MainButton,
  SecondaryButton,
  SmartInput,
} from '@web/ui';
import {
  backendErrorsToFieldErrors,
  formatReadonlyValue,
  getErrorMessage,
  notify,
  zodErrorsToFieldErrors,
} from '@web/utils';

import { WorkspaceDiscoverySection } from './WorkspaceDiscoverySection';
import styles from './WorkspaceSettingsPanel.module.scss';

import type {
  WorkspaceCurrencyOption,
  WorkspaceIndustryOption,
} from '@/features/workspaces/types';

type WorkspaceSettingsPanelProps = {
  canEdit: boolean;
};

type WorkspaceSettingsForm = {
  name: string;
  slug: string;
  industry_id: string;
  currency_code: string;
  logo_asset_key: string;
};

type WorkspaceSettingsField = keyof WorkspaceSettingsForm;
type FormErrors = Partial<Record<WorkspaceSettingsField, string>>;

function extractAssetKeyFromUrl(assetUrl?: string | null): string {
  if (!assetUrl) {
    return '';
  }

  try {
    const fallbackOrigin =
      typeof window !== 'undefined'
        ? window.location.origin
        : 'http://localhost';
    const parsedUrl = new URL(assetUrl, fallbackOrigin);
    const key =
      parsedUrl.searchParams.get('asset_key') ||
      parsedUrl.searchParams.get('key');
    return key ? decodeURIComponent(key) : '';
  } catch {
    return '';
  }
}

function normalizeWorkspaceSettingsForm(
  values: WorkspaceSettingsForm,
): WorkspaceSettingsForm {
  return {
    name: values.name.trim(),
    slug: values.slug.trim(),
    industry_id: values.industry_id || '',
    currency_code: values.currency_code || '',
    logo_asset_key: (values.logo_asset_key || '').trim(),
  };
}

function mapBackendErrorsToFormErrors(backendErrors: unknown): FormErrors {
  const flatErrors = backendErrorsToFieldErrors(backendErrors);
  const formErrors: FormErrors = {};
  const acceptedKeys: WorkspaceSettingsField[] = [
    'name',
    'slug',
    'industry_id',
    'currency_code',
    'logo_asset_key',
  ];

  acceptedKeys.forEach((key) => {
    if (flatErrors[key]) {
      formErrors[key] = flatErrors[key];
    }
  });

  return formErrors;
}

function extractApiErrorDetails(error: unknown): {
  message?: string;
  errors?: unknown;
} {
  if (
    typeof error === 'object' &&
    error !== null &&
    'data' in error &&
    typeof (error as { data?: unknown }).data === 'object' &&
    (error as { data?: unknown }).data !== null
  ) {
    const data = (error as { data: Record<string, unknown> }).data;
    return {
      message: typeof data.message === 'string' ? data.message : undefined,
      errors: data.errors,
    };
  }

  return {};
}

type WorkspaceSettingsPanelContentProps = {
  canEdit: boolean;
  currentWorkspace: any;
  fetchCurrencies: ReturnType<typeof useLazyGetWorkspaceCurrenciesQuery>[0];
  patchCurrentWorkspace: ReturnType<typeof usePatchCurrentWorkspaceMutation>[0];
  isSaving: boolean;
  requestStorageUpload: ReturnType<typeof useRequestStorageUploadMutation>[0];
};

function WorkspaceSettingsPanelContent({
  canEdit,
  currentWorkspace,
  fetchCurrencies,
  patchCurrentWorkspace,
  isSaving,
  requestStorageUpload,
}: WorkspaceSettingsPanelContentProps) {
  const resolvedLogoAssetKey =
    currentWorkspace?.logo_asset_key ||
    extractAssetKeyFromUrl(currentWorkspace?.logo_url) ||
    extractAssetKeyFromUrl(currentWorkspace?.avatar_url);
  const initialForm = normalizeWorkspaceSettingsForm({
    name: currentWorkspace?.name || '',
    slug: currentWorkspace?.slug || '',
    industry_id: currentWorkspace?.industry?.id || '',
    currency_code: currentWorkspace?.currency?.code || '',
    logo_asset_key: resolvedLogoAssetKey || '',
  });

  const [form, setForm] = useState<WorkspaceSettingsForm>(initialForm);
  const [initialFormSnapshot, setInitialFormSnapshot] =
    useState<WorkspaceSettingsForm>(initialForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [currencyInputValue, setCurrencyInputValue] = useState('');
  const [isRemovingLogo, setIsRemovingLogo] = useState(false);
  const [industryValue, setIndustryValue] =
    useState<WorkspaceIndustryOption | null>(
      currentWorkspace?.industry || null,
    );
  const [currencyValue, setCurrencyValue] =
    useState<WorkspaceCurrencyOption | null>(
      currentWorkspace?.currency || null,
    );
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(
    currentWorkspace?.logo_url || currentWorkspace?.avatar_url || '',
  );

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const blobPreviewUrlRef = useRef<string | null>(null);
  const currencyOptionsCacheRef = useRef<WorkspaceCurrencyOption[] | null>(
    null,
  );

  const requestUpload = useCallback(
    async (payload: {
      asset_kind: string;
      filename: string;
      content_type?: string;
    }) => {
      const response = await requestStorageUpload(payload).unwrap();

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Unable to prepare upload.');
      }

      return response.data;
    },
    [requestStorageUpload],
  );

  const { uploadAsset, isUploading } = useAssetUpload({
    requestUpload,
  });

  const isDirty = useMemo(() => {
    const normalizedCurrent = normalizeWorkspaceSettingsForm(form);
    const normalizedInitial =
      normalizeWorkspaceSettingsForm(initialFormSnapshot);

    return (
      normalizedCurrent.name !== normalizedInitial.name ||
      normalizedCurrent.slug !== normalizedInitial.slug ||
      normalizedCurrent.industry_id !== normalizedInitial.industry_id ||
      normalizedCurrent.currency_code !== normalizedInitial.currency_code ||
      normalizedCurrent.logo_asset_key !== normalizedInitial.logo_asset_key
    );
  }, [form, initialFormSnapshot]);
  useContextTabDirtyState(isDirty);
  const hasLogo = Boolean(form.logo_asset_key || logoPreviewUrl);
  const readonlyCurrencyLabel = currencyValue
    ? `${currencyValue.code} - ${currencyValue.label}`
    : form.currency_code;

  const setPreview = useCallback((nextUrl: string) => {
    if (blobPreviewUrlRef.current) {
      URL.revokeObjectURL(blobPreviewUrlRef.current);
      blobPreviewUrlRef.current = null;
    }

    if (nextUrl.startsWith('blob:')) {
      blobPreviewUrlRef.current = nextUrl;
    }

    setLogoPreviewUrl(nextUrl);
  }, []);

  useEffect(() => {
    return () => {
      if (blobPreviewUrlRef.current) {
        URL.revokeObjectURL(blobPreviewUrlRef.current);
        blobPreviewUrlRef.current = null;
      }
    };
  }, []);

  async function handleSave(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!canEdit) {
      return;
    }

    setErrors({});

    const parsedForm = workspaceSettingsSchema.safeParse({
      ...form,
      name: form.name.trim(),
      slug: form.slug.trim(),
    });
    if (!parsedForm.success) {
      setErrors(
        zodErrorsToFieldErrors<WorkspaceSettingsForm>(parsedForm.error),
      );
      return;
    }

    try {
      const previousSlug = currentWorkspace?.slug || '';
      const patchPayload: {
        name: string;
        slug: string;
        logo_asset_key?: string;
        industry_id: string | null;
        currency_code: string | null;
      } = {
        name: parsedForm.data.name,
        slug: parsedForm.data.slug,
        industry_id: parsedForm.data.industry_id || null,
        currency_code: parsedForm.data.currency_code || null,
      };

      const normalizedLogoAssetKey = (
        parsedForm.data.logo_asset_key || ''
      ).trim();
      if (normalizedLogoAssetKey) {
        patchPayload.logo_asset_key = normalizedLogoAssetKey;
      }

      const response = await patchCurrentWorkspace({
        ...patchPayload,
      }).unwrap();

      if (!response.success) {
        const fieldErrors = mapBackendErrorsToFormErrors(response.errors);
        if (Object.keys(fieldErrors).length > 0) {
          setErrors((previous) => ({ ...previous, ...fieldErrors }));
        }
        notify({
          type: 'error',
          message: response.message || 'Unable to update workspace.',
        });
        return;
      }

      notify({
        type: 'success',
        message: response.message || 'Workspace settings updated.',
      });

      const persistedForm = normalizeWorkspaceSettingsForm({
        name: response.data?.name || parsedForm.data.name,
        slug: response.data?.slug || parsedForm.data.slug,
        industry_id:
          response.data?.industry?.id || parsedForm.data.industry_id || '',
        currency_code:
          response.data?.currency?.code || parsedForm.data.currency_code || '',
        logo_asset_key:
          normalizedLogoAssetKey ||
          extractAssetKeyFromUrl(response.data?.logo_url) ||
          extractAssetKeyFromUrl(response.data?.avatar_url),
      });
      setForm((previous) => ({ ...previous, ...persistedForm }));
      setInitialFormSnapshot(persistedForm);

      const nextSlug = response.data?.slug;
      if (nextSlug && previousSlug && nextSlug !== previousSlug) {
        notify({
          type: 'info',
          message:
            'Workspace URL updated. You will see the new browser URL the next time you sign in.',
        });
      }
    } catch (error) {
      const errorDetails = extractApiErrorDetails(error);
      const fieldErrors = mapBackendErrorsToFormErrors(errorDetails.errors);
      if (Object.keys(fieldErrors).length > 0) {
        setErrors((previous) => ({ ...previous, ...fieldErrors }));
      }

      notify({
        type: 'error',
        message: errorDetails.message || getErrorMessage(error),
      });
    }
  }

  const handleLogoFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';

      if (!file) {
        return;
      }

      if (!file.type.startsWith('image/')) {
        notify({
          type: 'error',
          message: 'Please choose an image file for the workspace logo.',
        });
        return;
      }

      try {
        const assetKey = await uploadAsset({
          file,
          assetKind: 'workspace-logo',
        });
        const previewUrl = URL.createObjectURL(file);

        setForm((previous) => ({
          ...previous,
          logo_asset_key: assetKey,
        }));
        setPreview(previewUrl);
        notify({ type: 'success', message: 'Logo ready to save.' });
      } catch (error) {
        notify({ type: 'error', message: getErrorMessage(error) });
      }
    },
    [setPreview, uploadAsset],
  );

  const handleRemoveLogo = useCallback(async () => {
    if (!canEdit || isSaving || isUploading || isRemovingLogo || !hasLogo) {
      return;
    }

    setIsRemovingLogo(true);
    setErrors((previous) => ({
      ...previous,
      logo_asset_key: undefined,
    }));

    try {
      const response = await patchCurrentWorkspace({
        logo_asset_key: null,
      }).unwrap();

      if (!response.success) {
        const fieldErrors = mapBackendErrorsToFormErrors(response.errors);
        if (Object.keys(fieldErrors).length > 0) {
          setErrors((previous) => ({ ...previous, ...fieldErrors }));
        }
        notify({
          type: 'error',
          message: response.message || 'Unable to remove workspace logo.',
        });
        return;
      }

      setForm((previous) => ({
        ...previous,
        logo_asset_key: '',
      }));
      setInitialFormSnapshot((previous) => ({
        ...previous,
        logo_asset_key: '',
      }));
      setPreview('');

      notify({
        type: 'success',
        message: response.message || 'Workspace logo removed.',
      });
    } catch (error) {
      const errorDetails = extractApiErrorDetails(error);
      const fieldErrors = mapBackendErrorsToFormErrors(errorDetails.errors);
      if (Object.keys(fieldErrors).length > 0) {
        setErrors((previous) => ({ ...previous, ...fieldErrors }));
      }

      notify({
        type: 'error',
        message: errorDetails.message || getErrorMessage(error),
      });
    } finally {
      setIsRemovingLogo(false);
    }
  }, [
    canEdit,
    hasLogo,
    isRemovingLogo,
    isSaving,
    isUploading,
    patchCurrentWorkspace,
    setPreview,
  ]);

  const loadCurrencyOptions = useCallback(
    async ({ query }: { query: string; page: number }) => {
      if (!currencyOptionsCacheRef.current) {
        const response = await fetchCurrencies({ query: '' }, true).unwrap();

        currencyOptionsCacheRef.current =
          response.success && Array.isArray(response.data) ? response.data : [];
      }

      const options = currencyOptionsCacheRef.current ?? [];
      const normalizedQuery = query.trim().toLowerCase();

      return {
        options: normalizedQuery
          ? options.filter(
              (option) =>
                option.code.toLowerCase().includes(normalizedQuery) ||
                option.label.toLowerCase().includes(normalizedQuery),
            )
          : options,
        hasMore: false,
      };
    },
    [fetchCurrencies],
  );

  return (
    <section className={styles.workspaceSettingsPanel}>
      <form className={styles.form} onSubmit={handleSave}>
        <div className={styles.logoRow}>
          <Avatar
            name={form.name || 'Workspace'}
            src={logoPreviewUrl}
            size="xLarge"
            sx={{
              borderRadius: '50% !important',
            }}
          />
          {canEdit ? (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className={styles.hiddenFileInput}
                onChange={handleLogoFileSelect}
              />
              <MainButton
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading || isRemovingLogo}
                ariaLabel="Upload workspace logo"
              >
                {isUploading ? 'Uploading logo...' : 'Upload logo'}
              </MainButton>
              {hasLogo ? (
                <SecondaryButton
                  ariaLabel="Remove workspace logo"
                  onClick={() => {
                    void handleRemoveLogo();
                  }}
                >
                  {isRemovingLogo ? 'Removing logo...' : 'Remove logo'}
                </SecondaryButton>
              ) : null}
            </>
          ) : null}
        </div>

        {canEdit ? (
          <>
            <SmartInput
              type="text"
              name="workspace_name"
              label="Workspace name"
              value={form.name}
              onChange={(value) => {
                setForm((previous) => ({ ...previous, name: value }));
                if (errors.name) {
                  setErrors((previous) => ({ ...previous, name: undefined }));
                }
              }}
              onValidateChange={(_, error) =>
                setErrors((previous) => ({
                  ...previous,
                  name: error || undefined,
                }))
              }
              schema={workspaceSettingsSchema.shape.name}
              error={errors.name}
            />

            <SmartInput
              type="text"
              name="workspace_slug"
              label="Workspace URL"
              value={form.slug}
              onChange={(value) => {
                setForm((previous) => ({ ...previous, slug: value.trim() }));
                if (errors.slug) {
                  setErrors((previous) => ({ ...previous, slug: undefined }));
                }
              }}
              onValidateChange={(_, error) =>
                setErrors((previous) => ({
                  ...previous,
                  slug: error || undefined,
                }))
              }
              schema={workspaceSettingsSchema.shape.slug}
              error={errors.slug}
            />

            <IndustrySelectField
              value={industryValue}
              onChange={(value) => {
                setIndustryValue(value);
                setForm((previous) => ({
                  ...previous,
                  industry_id: value?.id || '',
                }));
                if (errors.industry_id) {
                  setErrors((previous) => ({
                    ...previous,
                    industry_id: undefined,
                  }));
                }
              }}
              error={errors.industry_id}
            />

            <AutocompleteAsync<WorkspaceCurrencyOption>
              value={currencyValue}
              loadOptions={loadCurrencyOptions}
              getOptionLabel={(option) => `${option.code} - ${option.label}`}
              isOptionEqualToValue={(option, value) =>
                option.code === value.code
              }
              onChange={(_event, value) => {
                setCurrencyValue(value);
                setForm((previous) => ({
                  ...previous,
                  currency_code: value?.code || '',
                }));
                if (errors.currency_code) {
                  setErrors((previous) => ({
                    ...previous,
                    currency_code: undefined,
                  }));
                }
              }}
              inputValue={currencyInputValue}
              onInputChange={(_event, value) => setCurrencyInputValue(value)}
              label="Currency"
              placeholder="Search currencies..."
              noOptionsText="No currencies found"
              textFieldProps={{
                error: Boolean(errors.currency_code),
                helperText: errors.currency_code,
              }}
            />
          </>
        ) : (
          <div className={styles.readonlyFields}>
            <div className={styles.readonlyField}>
              <p className="read-only-label">Workspace name</p>
              <p className="permission-readonly-value">
                {formatReadonlyValue(form.name)}
              </p>
            </div>
            <div className={styles.readonlyField}>
              <p className="read-only-label">Workspace URL</p>
              <p className="permission-readonly-value">
                {formatReadonlyValue(form.slug)}
              </p>
            </div>
            <div className={styles.readonlyField}>
              <p className="read-only-label">Industry</p>
              <p className="permission-readonly-value">
                {formatReadonlyValue(industryValue?.label)}
              </p>
            </div>
            <div className={styles.readonlyField}>
              <p className="read-only-label">Currency</p>
              <p className="permission-readonly-value">
                {formatReadonlyValue(readonlyCurrencyLabel)}
              </p>
            </div>
          </div>
        )}

        {canEdit ? (
          <div className={styles.actions}>
            <MainButton
              type="submit"
              isLoading={isSaving}
              disabled={!isDirty || isUploading || isRemovingLogo}
            >
              Save
            </MainButton>
          </div>
        ) : null}
      </form>
    </section>
  );
}

export function WorkspaceSettingsPanel({
  canEdit,
}: WorkspaceSettingsPanelProps) {
  const { data: currentWorkspaceResponse, isLoading: isWorkspaceLoading } =
    useGetCurrentWorkspaceQuery();
  const [fetchCurrencies] = useLazyGetWorkspaceCurrenciesQuery();
  const [patchCurrentWorkspace, { isLoading: isSaving }] =
    usePatchCurrentWorkspaceMutation();
  const [requestStorageUpload] = useRequestStorageUploadMutation();

  const currentWorkspace =
    currentWorkspaceResponse?.success === true
      ? currentWorkspaceResponse.data
      : null;
  const workspaceSignature = useMemo(
    () =>
      JSON.stringify({
        id: currentWorkspace?.id || '',
        name: currentWorkspace?.name || '',
        slug: currentWorkspace?.slug || '',
        industry_id: currentWorkspace?.industry?.id || '',
        currency_code: currentWorkspace?.currency?.code || '',
        logo_asset_key: currentWorkspace?.logo_asset_key || '',
        logo_url: currentWorkspace?.logo_url || '',
        avatar_url: currentWorkspace?.avatar_url || '',
      }),
    [currentWorkspace],
  );

  if (isWorkspaceLoading) {
    return (
      <section className={styles.workspaceSettingsPanel}>
        <div className={styles.loadingSkeletons}>
          <div className={styles.loadingLogoRow}>
            <Skeleton variant="circular" width={64} height={64} />
            <Skeleton variant="rounded" width={120} height={36} />
          </div>
          <Skeleton variant="rounded" width="100%" height={56} />
          <Skeleton variant="rounded" width="100%" height={56} />
          <Skeleton variant="rounded" width="100%" height={56} />
          <Skeleton variant="rounded" width="100%" height={56} />
          <div className={styles.loadingActionsSkeleton}>
            <Skeleton variant="rounded" width={80} height={40} />
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <WorkspaceSettingsPanelContent
        key={workspaceSignature}
        canEdit={canEdit}
        currentWorkspace={currentWorkspace}
        fetchCurrencies={fetchCurrencies}
        patchCurrentWorkspace={patchCurrentWorkspace}
        isSaving={isSaving}
        requestStorageUpload={requestStorageUpload}
      />
      <WorkspaceDiscoverySection />
    </>
  );
}
